#!/usr/bin/env node

/**
 * Sheesh Agent CLI
 * Command line interface for running AI agents
 */

import { Command } from 'commander';
import { spawn, ChildProcess } from 'child_process';
import path from 'path';
import fs from 'fs';
import chalk from 'chalk';
import dotenv from 'dotenv';

// Load environment variables
dotenv.config();

const program = new Command();

program
  .name('sheesh-agent')
  .description('AI Agent Framework with real-time broadcasting')
  .version('1.2.0');

program
  .command('start <agent-file>')
  .description('Start an AI agent')
  .option('-a, --admin', 'Start admin panel alongside agent')
  .option('-p, --admin-port <port>', 'Admin panel port', '8080')
  .option('--no-build', 'Skip building the framework')
  .action(async (agentFile, options) => {
    console.log(chalk.cyan.bold('\n🚀 Starting Sheesh Agent\n'));

    // Check if agent file exists
    const agentPath = path.resolve(agentFile);
    if (!fs.existsSync(agentPath)) {
      console.error(chalk.red(`❌ Agent file not found: ${agentPath}`));
      process.exit(1);
    }

    // Build framework if needed
    if (options.build !== false) {
      console.log(chalk.yellow('📦 Building framework...'));
      await runCommand('npm run build', process.cwd());
    }

    const processes: ChildProcess[] = [];

    // Start admin panel if requested
    if (options.admin) {
      console.log(chalk.yellow(`\n📊 Starting admin panel on port ${options.adminPort}...`));
      const adminPath = path.join(__dirname, '..', 'src', 'admin');
      
      // Install admin dependencies if needed
      if (!fs.existsSync(path.join(adminPath, 'node_modules'))) {
        console.log(chalk.dim('Installing admin dependencies...'));
        await runCommand('npm install', adminPath);
      }

      const adminProcess = spawn('npm', ['start'], {
        cwd: adminPath,
        env: { ...process.env, PORT: options.adminPort },
        stdio: 'inherit'
      });
      
      processes.push(adminProcess);
      
      console.log(chalk.green(`✓ Admin panel: http://localhost:${options.adminPort}`));
      console.log(chalk.dim(`  Token: ${process.env.ADMIN_TOKEN || 'Check your .env file'}\n`));
    }

    // Start the agent
    console.log(chalk.yellow('🤖 Starting agent...'));
    const agentProcess = spawn('node', [agentPath], {
      stdio: 'inherit',
      env: process.env
    });
    
    processes.push(agentProcess);

    // Handle shutdown
    const cleanup = () => {
      console.log(chalk.yellow('\n🛑 Shutting down...'));
      processes.forEach(proc => {
        try {
          proc.kill('SIGTERM');
        } catch (e) {
          // Process might already be dead
        }
      });
      process.exit(0);
    };

    process.on('SIGINT', cleanup);
    process.on('SIGTERM', cleanup);

    // Wait for processes
    agentProcess.on('exit', (code) => {
      console.log(chalk.yellow(`\nAgent exited with code ${code}`));
      cleanup();
    });
  });

program
  .command('build')
  .description('Build the framework')
  .action(async () => {
    console.log(chalk.yellow('📦 Building framework...'));
    await runCommand('npm run build', process.cwd());
    console.log(chalk.green('✓ Build complete'));
  });

program
  .command('admin')
  .description('Start only the admin panel')
  .option('-p, --port <port>', 'Port to run on', '8080')
  .action(async (options) => {
    console.log(chalk.cyan.bold('\n📊 Starting Admin Panel\n'));
    
    const adminPath = path.join(__dirname, '..', 'src', 'admin');
    
    // Install dependencies if needed
    if (!fs.existsSync(path.join(adminPath, 'node_modules'))) {
      console.log(chalk.dim('Installing admin dependencies...'));
      await runCommand('npm install', adminPath);
    }

    console.log(chalk.green(`Admin panel: http://localhost:${options.port}`));
    console.log(chalk.dim(`Token: ${process.env.ADMIN_TOKEN || 'Check your .env file'}\n`));

    const adminProcess = spawn('npm', ['start'], {
      cwd: adminPath,
      env: { ...process.env, PORT: options.port },
      stdio: 'inherit'
    });

    adminProcess.on('exit', (code) => {
      console.log(chalk.yellow(`\nAdmin panel exited with code ${code}`));
      process.exit(code);
    });
  });

program
  .command('init')
  .description('Initialize a new agent project')
  .action(() => {
    console.log(chalk.cyan.bold('\n🎉 Initializing new Sheesh agent project\n'));
    
    // Copy .env.example to .env if it doesn't exist
    if (!fs.existsSync('.env')) {
      const envExample = path.join(__dirname, '..', '.env.example');
      if (fs.existsSync(envExample)) {
        fs.copyFileSync(envExample, '.env');
        console.log(chalk.green('✓ Created .env file'));
        console.log(chalk.yellow('  Please edit .env and add your API keys'));
      }
    }

    // Create example agent if it doesn't exist
    const exampleAgent = `import { AgentController } from '@phantasy/sheesh-agent';
import { createSheeshPlugin } from '@phantasy/sheesh-agent/plugins/sheesh';
import { MahoText } from '@phantasy/sheesh-agent/providers/text/maho';
import dotenv from 'dotenv';

dotenv.config();

async function startAgent() {
  // Initialize provider
  const textProvider = new MahoText();
  await textProvider.initialize({
    apiKey: process.env.MAHO_API_KEY || process.env.OPENAI_API_KEY,
    model: 'gpt-3.5-turbo'
  });

  // Create controller
  const controller = new AgentController({
    providers: { text: textProvider },
    personality: {
      name: 'MyAgent',
      traits: ['helpful', 'friendly'],
      tone: 'casual'
    }
  });

  // Create plugin
  const plugin = createSheeshPlugin({
    partyKitUrl: process.env.PARTYKIT_URL || 'ws://localhost:1999',
    roomId: 'my-room',
    agentId: 'my-agent-001',
    agentName: 'MyAgent',
    onUserMessage: async (userId, message) => {
      const response = await controller.processUserMessage(userId, message);
      plugin.broadcastResponse(response);
    }
  });

  // Initialize and connect
  await plugin.initialize();
  console.log('🚀 Agent is online and broadcasting!');
}

startAgent().catch(console.error);
`;

    if (!fs.existsSync('my-agent.js')) {
      fs.writeFileSync('my-agent.js', exampleAgent);
      console.log(chalk.green('✓ Created example agent: my-agent.js'));
    }

    console.log(chalk.cyan('\nNext steps:'));
    console.log('1. Edit .env and add your API keys');
    console.log('2. Customize my-agent.js');
    console.log('3. Run: sheesh-agent start my-agent.js --admin');
    console.log('   Or: sheesh start my-agent.js --admin');
  });

// Helper function
async function runCommand(command: string, cwd: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const proc = spawn(command, {
      shell: true,
      cwd,
      stdio: 'inherit'
    });
    
    proc.on('exit', (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(new Error(`Command failed with code ${code}`));
      }
    });
  });
}

program.parse();